/**
 * Native-platform detection (Capacitor).
 *
 * `isNativePlatform()` is `false` in the Vercel web/PWA build and `true` once
 * this same bundle is wrapped by the `lighthouse-mobile` Capacitor shell.
 * Gate any native-only behavior (OAuth via in-app browser, native push,
 * haptics, safe areas, route scope) behind these helpers so a single
 * codebase serves both targets with no manual porting step.
 */
import { Capacitor } from '@capacitor/core'

export function isNativePlatform(): boolean {
    return Capacitor.isNativePlatform()
}

/**
 * True inside the Electron desktop shell. The shell's user agent includes
 * `Electron/`, which the hosted web app can see without a custom bridge, and
 * the main window also gets `window.lighthouseDesktop`.
 * Use this for route scope (skip marketing, open the standalone login).
 * Do not treat it as Capacitor — desktop has no native plugins. Anything that
 * calls into the shell goes through `getDesktopBridge()` (src/desktop/bridge).
 */
export function isDesktopShell(): boolean {
    if (typeof window !== 'undefined' && window.lighthouseDesktop) return true
    if (typeof navigator === 'undefined') return false
    return /\bElectron\//.test(navigator.userAgent)
}

export function getNativePlatform(): 'ios' | 'android' | 'web' {
    return Capacitor.getPlatform() as 'ios' | 'android' | 'web'
}

export function isIosNative(): boolean {
    return getNativePlatform() === 'ios'
}

export function isAndroidNative(): boolean {
    return getNativePlatform() === 'android'
}

/**
 * True for native app OR touch-primary browsers (mobile web/PWA). Use this
 * (rather than `isNativePlatform`) for interaction-model decisions like
 * hover-vs-tap, since mobile web/PWA users hit the same problem.
 */
export function isTouchPrimary(): boolean {
    if (isNativePlatform()) return true
    if (typeof window === 'undefined') return false
    return window.matchMedia?.('(hover: none), (pointer: coarse)')?.matches === true
}
